import { 
  Alert, 
  AlertTitle, 
  Badge, 
  Box, 
  Grid, 
  Typography, 
  Paper,
  Card,
  Chip,
  Button,
  useTheme,
  alpha,
  Fade
} from "@mui/material";
import useCheckPendencia from "../../hooks/useCheckPendencia";
import AlertCustomFixed from "../AlertCustomFixed";
import useAlert from "../../hooks/useAlert";
import { useEffect } from "react";
import TiposPendencia from "../../util/typesTiposPendencia";
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

function PendenciaCliente({
  handlerPanelDocumentos,
  handlerPanelDadosCadastrais,
}) {
  const { pendencias, messageError } = useCheckPendencia();
  const { showAlertError } = useAlert();
  const theme = useTheme();

  useEffect(() => {
    if (!!messageError) showAlertError(messageError);
  }, [messageError]);

  const handlerChangePanels = (pendencia) => {
    if (!pendencia.hasOwnProperty("tipoPendencia")) {
      console.error("Não consta a propriedade tipoPendencia");
      return;
    }

    if (pendencia?.tipoPendencia?.id === TiposPendencia.documentos)
      handlerPanelDocumentos();
    if (pendencia?.tipoPendencia?.id === TiposPendencia.dadosCadastrais)
      handlerPanelDadosCadastrais();
  };

  const getSeverityIcon = (impedeOperar) => {
    return impedeOperar ? <ErrorIcon /> : <WarningIcon />;
  };

  const getSeverityColor = (impedeOperar) => {
    return impedeOperar ? theme.palette.error.main : theme.palette.warning.main;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header com informações */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.2)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoIcon color="info" sx={{ fontSize: 32, mt: 0.5 }} />
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.info.dark }}>
              Status do Seu Cadastro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Se você já tomou as medidas necessárias, por favor, aguarde enquanto nossa equipe verifica e atualiza suas informações.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Legenda de status */}
      <Card 
        elevation={1}
        sx={{ 
          p: 2, 
          mb: 4, 
          borderRadius: 2,
          background: theme.palette.background.paper
        }}
      >
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Legenda de Status:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.warning.main,
              }}
            />
            <Typography variant="body2">Atenção - Não impede operar</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.error.main,
              }}
            />
            <Typography variant="body2">Urgente - Impede operar</Typography>
          </Box>
        </Box>
      </Card>

      {/* Lista de pendências */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3, color: theme.palette.text.primary }}>
          {pendencias?.length > 0 ? 'Pendências Identificadas' : 'Status do Cadastro'}
        </Typography>

        {pendencias?.length > 0 ? (
          <Grid container spacing={2}>
            {pendencias.map((pendencia, index) => (
              <Grid item xs={12} key={`${index}pen`}>
                <Fade in timeout={800 + (index * 100)}>
                  <Card
                    elevation={2}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderLeft: `4px solid ${getSeverityColor(pendencia.impedeOperar)}`,
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          color: getSeverityColor(pendencia.impedeOperar),
                          mt: 0.5
                        }}
                      >
                        {getSeverityIcon(pendencia.impedeOperar)}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                          <AlertTitle sx={{ 
                            mb: 1, 
                            color: getSeverityColor(pendencia.impedeOperar),
                            fontSize: '1.1rem'
                          }}>
                            {pendencia.descricao}
                          </AlertTitle>
                          
                          <Chip
                            label={pendencia.impedeOperar ? "IMPEDE OPERAR" : "ATENÇÃO"}
                            size="small"
                            color={pendencia.impedeOperar ? "error" : "warning"}
                            variant="filled"
                            icon={pendencia.impedeOperar ? <PriorityHighIcon /> : <WarningIcon />}
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
                          {pendencia.observacao}
                        </Typography>
                        
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={() => handlerChangePanels(pendencia)}
                          startIcon={<CheckCircleIcon />}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            background: pendencia.impedeOperar 
                              ? `linear-gradient(45deg, ${theme.palette.error.main} 30%, ${theme.palette.error.dark} 90%)`
                              : `linear-gradient(45deg, ${theme.palette.warning.main} 30%, ${theme.palette.warning.dark} 90%)`,
                            '&:hover': {
                              boxShadow: pendencia.impedeOperar 
                                ? `0 4px 8px ${alpha(theme.palette.error.main, 0.4)}`
                                : `0 4px 8px ${alpha(theme.palette.warning.main, 0.4)}`,
                            }
                          }}
                        >
                          Resolver Agora
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.2)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}
          >
            <CheckCircleIcon
              color="success"
              sx={{ fontSize: 48, mb: 2 }}
            />
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.success.dark, fontWeight: 600 }}>
              Cadastro em Dia!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Você não possuí nenhuma pendência em seu cadastro.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default PendenciaCliente;